/**
 * Centralized, type-safe route definitions.
 * All navigation in the app must use these constants — no raw string paths.
 */

export const ROUTES = {
  // Public (unauthenticated)
  login: '/login',
  register: '/register',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  changePassword: '/change-password',
  partnerRegister: '/partner-register',
  privacy: '/privacy',
  security: '/security',

  // Protected (authenticated, inside Layout)
  upload: '/upload',
  documents: '/documents',
  documentDetail: (id: string) => `/documents/${id}` as const,
  // Matter-scoped document detail — per B-UI.0 audit §4.4 + ADR-082 §"matter-as-spine" design invariant.
  // Preferred entry point from MatterDocumentsList. Keeps global /documents/:id for backward-compat bookmarks.
  matterDocumentDetail: (matterId: string, documentId: string) =>
    `/matters/${matterId}/documents/${documentId}` as const,
  classify: '/classify',
  validation: '/validation',
  gapAnalysis: '/gap-analysis',
  dashboard: '/dashboard',
  workbench: '/workbench',
  intake: '/intake',
  createMatter: '/create-matter',
  matters: '/matters',
  matterDetail: (id: string) => `/matters/${id}` as const,
  profile: '/profile',
  report: '/report',
  reportDetail: (id: string) => `/reports/${id}` as const,
  sourceConnections: '/settings/source-connections',
  emailConnections: '/settings/email-connections',
  watchedFolders: '/settings/watched-folders',
  encryptionSettings: '/settings/encryption',
  encryptionAuditLog: '/settings/security/access-log',
  complianceReports: '/settings/compliance-reports',
  byokSettings: '/settings/encryption/byok',
  attestationReports: '/settings/security/attestation',
  auditorDashboard: '/auditor',
  admin: '/admin',
  adminUsers: '/admin/users',
  adminTeams: '/admin/teams',
  adminSettings: '/admin/settings',
  adminAuditLog: '/admin/audit-log',
  // Break-glass / support access management (customer_admin + admin)
  // Per ADR-082 §"Edge case E1" + Wave 3.5 Pkg E FE (Option A: admin sub-route)
  adminSupportAccess: '/admin/support-access',

  brandingSettings: '/settings/branding',
  apiKeys: '/settings/api-keys',
  usageDashboard: '/settings/usage',
  emailMonitors: '/settings/email-monitors',
  matterEmails: (matterId: string) => `/matters/${matterId}/emails` as const,

  // Baselines
  baselines: '/baselines',
  baselineDetail: (id: string) => `/baselines/${id}` as const,

  scheduledReports: '/settings/scheduled-reports',

  // VDR (Virtual Data Room)
  vdr: '/settings/vdr',
  vdrProvider: (provider: string) => `/settings/vdr/${provider}` as const,
  vdrAudit: (provider: string) => `/settings/vdr/${provider}/audit` as const,

  // RAG Search
  ragSearch: '/search',

  // Root (redirects to upload)
  root: '/',
} as const;
